import { SettingSection } from '@/components/settings/SettingSection';
import { useEffect, useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { Colors } from '@/constants/theme';
import { useColorScheme } from '@/hooks/use-color-scheme';

interface PreferredNameEditorProps {
  initialName: string;
  accentColor: string;
  textColor: string;
  placeholderColor: string;
  backgroundColor: string;
  onSave: (name: string) => void;
  isSaving?: boolean;
}

/**
 * PreferredNameEditor Component
 * Allows users to edit their preferred name with validation and save functionality.
 */
export function PreferredNameEditor({
  initialName,
  accentColor,
  textColor,
  placeholderColor,
  backgroundColor,
  onSave,
  isSaving = false,
}: PreferredNameEditorProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const [preferredName, setPreferredName] = useState(initialName);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setHasChanges(preferredName !== initialName);
  }, [preferredName, initialName]);

  const handleSave = () => {
    if (hasChanges && !isSaving) {
      onSave(preferredName);
    }
  };

  return (
    <SettingSection title="PREFERRED NAME" textColor={textColor}>
      <View style={[styles.container, { backgroundColor }]}>
        <View style={styles.inputContainer}>
          <TextInput
            style={[styles.textInput, { color: textColor, backgroundColor: palette.cardBackground, borderColor: palette.border }]}
            value={preferredName}
            onChangeText={setPreferredName}
            placeholder="Enter your preferred name"
            placeholderTextColor={placeholderColor}
            editable={!isSaving}
          />
        </View>

        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[
              styles.saveButton,
              { backgroundColor: accentColor },
              (!hasChanges || isSaving) && styles.saveButtonDisabled,
            ]}
            disabled={!hasChanges || isSaving}
            onPress={handleSave}
            activeOpacity={0.7}>
            <Text style={styles.saveButtonText}>
              {isSaving ? 'Saving...' : 'Save'}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </SettingSection>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: BorderRadius.md,
    padding: Spacing.lg,
  },
  inputContainer: {
    marginBottom: Spacing.md,
  },
  textInput: {
    fontSize: 16,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.md, // normalized from 14
    borderRadius: BorderRadius.md,
    borderWidth: 1,
  },
  buttonContainer: {
    marginBottom: Spacing.sm,
  },
  saveButton: {
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.xxl,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.4,
  },
  saveButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  helperText: {
    fontSize: 13,
    textAlign: 'center',
    marginTop: Spacing.xs,
  },
});
